/**
 * AcceptInvite — public staff-onboarding page (Workstream B).
 *
 * Reached from the invite email link `…/accept-invite?token=<raw>`. No session:
 * the token IS the credential. POSTs to identity-svc `/identity/staff/accept`,
 * which creates the Aurora user + Auth0 directory entry and returns a
 * password-change ticket URL. On success we redirect the staff member there to
 * set their credential (then they're bounced to the admin SSO login).
 *
 * Rendered OUTSIDE the SSO AuthGate (see App.tsx) — the invitee has no account
 * yet. It needs only the QueryClient + the anonymous api client.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { staffService } from "@re-platform/api-client";
import { PhoneInput } from "@re-platform/ui";
import { api } from "../../lib/api";

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

export function AcceptInvite() {
  const token = tokenFromUrl();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [license, setLicense] = useState("");

  // The page is public, so it cannot read the caller's tenant — the invite
  // token is the only thing that knows which brokerage this is. Without it the
  // form asked every invitee for a TREC licence, including mortgage agents who
  // hold an NMLS number and no TREC licence at all.
  const invite = useQuery({
    queryKey: ["invite-lookup", token],
    queryFn: () => staffService.lookupInvitation(api, token),
    enabled: token.length > 0,
    retry: false,
  });
  const isMortgage = invite.data?.brokerage_type === "MORTGAGE";

  const mutation = useMutation({
    mutationFn: () =>
      staffService.acceptInvitation(api, {
        token,
        full_name: fullName.trim() || undefined,
        phone: phone.trim() || undefined,
        // Exactly one of these, decided by the brokerage — never both, so a
        // number can't land in the wrong regulator's column.
        ...(isMortgage
          ? { nmls_id: license.trim() || undefined }
          : { trec_license_number: license.trim() || undefined }),
      }),
    onSuccess: (res) => {
      if (res.credential_set_url) {
        // Send them to Auth0 to set their password (→ admin login afterwards).
        window.location.href = res.credential_set_url;
      }
    },
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          border: "1px solid var(--rp-color-border)",
          borderRadius: "var(--rp-radius-md, 8px)",
          padding: 28,
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: 22 }}>Accept your invitation</h1>

        {!token ? (
          <p style={{ color: "var(--rp-color-danger, red)" }}>
            This link is missing its invitation token. Please use the link from
            your invitation email.
          </p>
        ) : mutation.isSuccess && !mutation.data.credential_set_url ? (
          <p style={{ color: "var(--rp-color-success, green)" }}>
            Your account is set up. Your administrator will share how to sign
            in.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!mutation.isPending) mutation.mutate();
            }}
          >
            <p
              style={{
                color: "var(--rp-color-text-muted)",
                fontSize: 14,
                marginTop: 0,
              }}
            >
              Tell us a little about you, then we'll take you to set your
              password.
            </p>
            <Field label="Full name">
              <input
                value={fullName}
                placeholder="Ann Agent"
                onChange={(e) => setFullName(e.target.value)}
                style={field}
              />
            </Field>
            <Field label="Phone">
              {/* Stored as E.164 and shown as (512) 555-0142. The hint below is
                  not decoration: a dual-licensed agent is matched across
                  brokerages on this exact string (users.phone_e164), so two
                  spellings of one number are two different people to us. */}
              <PhoneInput
                className=""
                value={phone}
                onChange={(e164) => setPhone(e164 ?? "")}
                style={field}
              />
              <span
                style={{
                  color: "var(--rp-color-text-muted)",
                  fontSize: 12,
                }}
              >
                Licensed in more than one brokerage? Use the SAME phone number
                on every invite you accept — that's how we link them as one
                dual-licensed agent. Leave this blank if you already gave it on
                a different invite; we'll reuse it.
              </span>
            </Field>
            <Field
              label={
                isMortgage
                  ? "NMLS ID (agents only)"
                  : "TREC license number (agents only)"
              }
            >
              <input
                value={license}
                placeholder="optional"
                onChange={(e) => setLicense(e.target.value)}
                style={field}
              />
            </Field>
            <button
              type="submit"
              className="rp-btn rp-btn--primary"
              style={{ width: "100%", marginTop: 8 }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Setting up…" : "Accept invitation"}
            </button>
            {mutation.isError && (
              <p
                style={{
                  color: "var(--rp-color-danger, red)",
                  fontSize: 13,
                  marginBottom: 0,
                }}
              >
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : "This invitation could not be accepted. It may be expired or already used."}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

const field: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 14,
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontSize: 13,
        marginBottom: 14,
      }}
    >
      <span style={{ color: "var(--rp-color-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}
